<template>
  <div id="gap-backtest-stock-universe">
    <div class="container-fluid pt-4 px-4">
      <v-alert type="info" variant="tonal" density="comfortable" class="mb-4" rounded="lg">
        Onboards stocks into the strategy's candidate universe -- a separate pool from your portfolio, tracked only so
        the gap backtest can rank them. An onboarded stock never trades on its own: add it to the
        <router-link to="/gap-backtest/watchlist">Watchlist</router-link> once you're happy with its backtest score.
        <strong>No verified NIFTY 500 constituent list is loaded yet</strong>, so paste the codes you want to check
        below rather than picking a broad index -- see STOCK_UNIVERSE.md.
      </v-alert>

      <v-card flat rounded="lg" class="gap-backtest-card mb-4">
        <v-card-title class="px-5 py-4">
          <span class="text-h6 font-weight-medium">Onboard</span>
        </v-card-title>
        <v-card-text class="px-5 pb-5">
          <v-row dense>
            <v-col cols="12" md="3">
              <!-- No auto-fill here yet (unlike the sector picker below) --
                   GET /onboard/indices only returns the verified market_index
                   registry, not constituent codes; a real per-index candidate
                   list doesn't exist the way nifty-constituents.js's industry
                   grouping does for sector. Deliberately deferred, not an
                   oversight -- see STOCK_UNIVERSE.md's "Still needed". -->
              <v-combobox
                v-model="indexModel"
                :items="indexOptions"
                item-title="label"
                item-value="code"
                label="Source index (label only)"
                hint="Pick a verified one, or type a new label (e.g. NIFTY500)"
                persistent-hint
                density="comfortable"
                variant="outlined"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-combobox
                v-model="sectorModel"
                :items="sectorOptions"
                item-title="label"
                item-value="label"
                label="Sector (optional)"
                hint="Pick a known sector to auto-fill its codes, or type a custom label"
                persistent-hint
                density="comfortable"
                variant="outlined"
                clearable
              />
            </v-col>
            <v-col cols="4" md="2">
              <v-text-field v-model.number="form.minPrice" type="number" label="Min price" density="comfortable" variant="outlined" hide-details />
            </v-col>
            <v-col cols="4" md="2">
              <v-text-field v-model.number="form.maxPrice" type="number" label="Max price" density="comfortable" variant="outlined" hide-details />
            </v-col>
            <v-col cols="4" md="2">
              <v-text-field v-model.number="form.years" type="number" label="History years" density="comfortable" variant="outlined" hide-details />
            </v-col>
          </v-row>
          <v-row dense class="mt-2">
            <v-col cols="12">
              <v-textarea
                v-model="form.codesRaw"
                label="Codes (comma or newline separated)"
                rows="3"
                density="comfortable"
                variant="outlined"
                hide-details
                placeholder="SBIN, ITC, TATASTEEL"
              />
            </v-col>
          </v-row>
          <div class="d-flex align-center ga-3 mt-4">
            <v-btn color="primary" variant="tonal" :loading="previewing" @click="runPreview">Preview</v-btn>
            <v-btn
              color="success"
              variant="tonal"
              :disabled="!eligibleCodes.length || marketHoursBlocked"
              :loading="starting"
              @click="runStart"
            >
              Start onboarding ({{ eligibleCodes.length }} eligible)
            </v-btn>
            <span v-if="marketHoursBlocked" class="text-caption text-warning">
              Blocked during market hours (09:00-15:40 IST) -- backfilling competes with the live session for the same API budget.
            </span>
          </div>
          <div v-if="previewError" class="text-caption text-error mt-2">{{ previewError }}</div>
        </v-card-text>
      </v-card>

      <v-card v-if="preview" flat rounded="lg" class="gap-backtest-card mb-4">
        <v-card-title class="d-flex justify-space-between align-center flex-wrap ga-3 px-5 py-4">
          <span class="text-h6 font-weight-medium">Preview funnel</span>
          <div class="d-flex align-center ga-3">
            <span class="text-caption text-medium-emphasis">{{ preview.total }} code(s) checked</span>
            <v-btn
              v-if="unlinkedAlreadyOnboardedCount > 0"
              size="small" variant="tonal" color="primary" :loading="linking"
              @click="linkExistingToIndex"
            >
              Link {{ unlinkedAlreadyOnboardedCount }} to "{{ form.index }}"
            </v-btn>
            <v-btn
              v-if="form.sector && unlinkedAlreadyOnboardedForSectorCount > 0"
              size="small" variant="tonal" color="primary" :loading="linkingSector"
              @click="linkExistingToSectorAction"
            >
              Link {{ unlinkedAlreadyOnboardedForSectorCount }} to sector "{{ form.sector }}"
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text class="px-5 pb-3">
          <v-row dense class="mb-2">
            <v-col cols="6" sm="2" v-for="stat in funnelStats" :key="stat.key">
              <div class="text-caption text-medium-emphasis">{{ stat.label }}</div>
              <div class="text-h6" :class="stat.color">{{ preview.counts[stat.key] || 0 }}</div>
            </v-col>
          </v-row>
          <div v-if="linkError" class="text-caption text-error">{{ linkError }}</div>
          <div v-if="linkResult" class="text-caption text-success">
            Linked {{ linkResult.linked }} stock{{ linkResult.linked === 1 ? '' : 's' }} to "{{ form.index }}".
          </div>
          <div v-if="linkSectorError" class="text-caption text-error">{{ linkSectorError }}</div>
          <div v-if="linkSectorResult" class="text-caption text-success">
            Linked {{ linkSectorResult.linked }} stock{{ linkSectorResult.linked === 1 ? '' : 's' }} to sector "{{ form.sector }}".
          </div>
        </v-card-text>
        <v-data-table
          :headers="previewHeaders"
          :items="preview.rows"
          item-value="code"
          fixed-header
          :items-per-page="-1"
          hide-default-footer
          class="custom-data-table"
        >
          <template v-slot:[`item.price`]="{ item }">
            <span v-if="item.price !== null">{{ $helper.formatPrice(item.price) }}</span>
            <span v-else class="text-medium-emphasis">—</span>
          </template>
          <template v-slot:[`item.hasToken`]="{ item }">
            <font-awesome-icon v-if="item.hasToken === true" icon="fa-solid fa-check" class="text-success" />
            <font-awesome-icon v-else-if="item.hasToken === false" icon="fa-solid fa-xmark" class="text-error" />
            <span v-else class="text-medium-emphasis">—</span>
          </template>
          <template v-slot:[`item.intradayAllowed`]="{ item }">
            <font-awesome-icon v-if="item.intradayAllowed === true" icon="fa-solid fa-check" class="text-success" />
            <font-awesome-icon v-else-if="item.intradayAllowed === false" icon="fa-solid fa-xmark" class="text-error" />
            <span v-else class="text-medium-emphasis">—</span>
          </template>
          <template v-slot:[`item.result`]="{ item }">
            <v-chip size="small" variant="tonal" :color="resultColor(item.result)">{{ resultLabel(item.result) }}</v-chip>
            <div v-if="item.linkedIndexes && item.linkedIndexes.length" class="text-caption text-medium-emphasis mt-1">
              in: {{ item.linkedIndexes.join(', ') }}
              <span v-if="item.alreadyLinkedToThisIndex" class="text-success">(incl. this one)</span>
            </div>
            <div v-if="item.linkedSectors && item.linkedSectors.length" class="text-caption text-medium-emphasis mt-1">
              sector: {{ item.linkedSectors.join(', ') }}
              <span v-if="item.alreadyLinkedToThisSector" class="text-success">(incl. this one)</span>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <v-card v-if="run" flat rounded="lg" class="gap-backtest-card mb-4">
        <v-card-title class="px-5 py-4">
          <span class="text-h6 font-weight-medium">Onboarding progress</span>
        </v-card-title>
        <v-card-text class="px-5 pb-5">
          <v-progress-linear :model-value="runProgressPct" height="18" color="primary" rounded>
            <span class="text-caption">{{ run.done }} / {{ run.total }}</span>
          </v-progress-linear>
          <div class="text-caption text-medium-emphasis mt-2">
            <span v-if="run.running">In progress{{ run.currentCode ? ` -- currently ${run.currentCode}` : '' }}...</span>
            <span v-else-if="run.finishedAt">
              Finished. {{ run.onboarded }} onboarded.
              <template v-if="run.storedInactive">{{ run.storedInactive }} stored inactive (out of range / intraday-restricted).</template>
            </span>
          </div>
          <div v-if="run.errors && run.errors.length" class="text-caption text-error mt-2">
            <div v-for="(e, i) in run.errors" :key="i">{{ e.code || 'run' }}: {{ e.message }}</div>
          </div>
        </v-card-text>
      </v-card>

      <v-card flat rounded="lg" class="gap-backtest-card">
        <v-card-title class="d-flex justify-space-between align-center flex-wrap ga-3 px-5 py-4">
          <span class="text-h6 font-weight-medium">Current universe</span>
          <div class="d-flex align-center ga-3">
            <v-select
              v-model="sectorFilter"
              :items="distinctSectors"
              label="Filter by sector"
              clearable
              density="compact"
              variant="outlined"
              hide-details
              style="max-width: 220px"
            />
            <!-- Out-of-range / intraday-restricted codes are now stored too
                 (inactive, never fetched/backtested -- see runOnboardingAsync)
                 so a picked index or sector shows its complete list. Hidden
                 by default so the grid stays focused on tradeable stock;
                 toggle on to see the full stored set. -->
            <v-switch
              v-model="showInactive"
              label="Show inactive"
              density="compact"
              hide-details
              color="primary"
            />
            <span class="text-caption text-medium-emphasis">{{ filteredUniverse.length }} stock{{ filteredUniverse.length === 1 ? '' : 's' }}</span>
            <v-btn variant="tonal" color="primary" size="small" :loading="runningBacktest" @click="runBacktestNow">
              Run Backtest Now
            </v-btn>
            <v-btn variant="text" size="small" :loading="loadingUniverse" @click="loadUniverse">
              <font-awesome-icon icon="fa-solid fa-rotate-right" class="mr-1" /> Refresh
            </v-btn>
          </div>
        </v-card-title>
        <div v-if="backtestSummary" class="px-5 pb-3 text-caption text-medium-emphasis">
          Last run: {{ backtestSummary.stocksProcessed }} stocks processed, {{ backtestSummary.totalGapEvents }} gap events
          ({{ backtestSummary.stocksSkipped }} skipped — not enough history yet).
        </div>
        <div v-if="backtestError" class="px-5 pb-3 text-caption text-error">{{ backtestError }}</div>
        <v-data-table
          :headers="universeHeaders"
          :items="filteredUniverse"
          item-value="code"
          fixed-header
          :items-per-page="-1"
          hide-default-footer
          class="custom-data-table"
        >
          <template v-slot:[`item.sourceIndex`]="{ item }">
            <span v-if="item.indices && item.indices.length">
              <span v-for="(idx, i) in item.indices" :key="idx.label">
                <span v-if="i > 0">, </span>
                <span v-if="idx.membershipType === 'affected'" class="text-medium-emphasis" title="Heuristic: industry-based guess, not a verified constituent">{{ idx.label }}*</span>
                <span v-else>{{ idx.label }}</span>
              </span>
            </span>
            <span v-else class="text-medium-emphasis">{{ item.sourceIndex || '—' }}</span>
          </template>
          <template v-slot:[`item.sector`]="{ item }">
            <span v-if="item.sectors && item.sectors.length">{{ item.sectors.map((s) => s.label).join(', ') }}</span>
            <span v-else class="text-medium-emphasis">—</span>
          </template>
          <template v-slot:[`item.onboardPrice`]="{ item }">
            <span v-if="item.onboardPrice !== null">{{ $helper.formatPrice(item.onboardPrice) }}</span>
            <span v-else class="text-medium-emphasis">—</span>
          </template>
          <template v-slot:[`item.status`]="{ item }">
            <v-chip size="small" variant="tonal" :color="universeStatusColor(item.status)">{{ item.status }}</v-chip>
          </template>
          <template v-slot:no-data>
            <div class="py-6 text-center text-medium-emphasis">Nothing onboarded yet.</div>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </div>
</template>

<script>
import { fetchWrapper } from '@/helpers/fetch-wrapper';

const RESULT_LABELS = {
  eligible: 'Eligible',
  alreadyOnboarded: 'Already onboarded',
  skippedNoToken: 'No token',
  skippedIntradayRestricted: 'Intraday restricted',
  outOfBand: 'Outside price band',
};
const RESULT_COLORS = {
  eligible: 'success',
  alreadyOnboarded: 'default',
  skippedNoToken: 'error',
  skippedIntradayRestricted: 'error',
  outOfBand: 'warning',
};

export default {
  name: 'GapBacktestStockUniverse-page',
  data() {
    return {
      form: { index: 'CUSTOM', sector: '', minPrice: 100, maxPrice: 500, years: 3, codesRaw: '' },
      indexOptions: [{ code: 'CUSTOM', label: 'Custom (pasted codes)', name: 'Custom' }],
      sectorOptions: [],
      sectorFilter: null,
      showInactive: false,
      preview: null,
      previewing: false,
      previewError: null,
      linking: false,
      linkError: null,
      linkResult: null,
      linkingSector: false,
      linkSectorError: null,
      linkSectorResult: null,
      run: null,
      starting: false,
      pollTimer: null,
      universe: [],
      loadingUniverse: true,
      runningBacktest: false,
      backtestSummary: null,
      backtestError: null,
      now: new Date(),
      clockTimer: null,
      previewHeaders: [
        { title: 'Code', key: 'code' },
        { title: 'Price', key: 'price' },
        { title: 'Token', key: 'hasToken' },
        { title: 'Intraday', key: 'intradayAllowed' },
        { title: 'Result', key: 'result' },
      ],
      universeHeaders: [
        { title: 'Code', key: 'code' },
        { title: 'Source', key: 'sourceIndex' },
        { title: 'Sector', key: 'sector' },
        { title: 'Onboard Price', key: 'onboardPrice' },
        { title: 'History Rows', key: 'historyCount' },
        { title: 'Gap Events', key: 'gapEventCount' },
        { title: 'Status', key: 'status' },
      ],
      funnelStats: [
        { key: 'eligible', label: 'Eligible', color: 'text-success' },
        { key: 'alreadyOnboarded', label: 'Already onboarded', color: '' },
        { key: 'skippedNoToken', label: 'No token', color: 'text-error' },
        { key: 'skippedIntradayRestricted', label: 'Intraday restricted', color: 'text-error' },
        { key: 'outOfBand', label: 'Outside band', color: 'text-warning' },
      ],
    };
  },
  computed: {
    // v-combobox emits a plain string when the operator TYPES a new label,
    // but the full item object when one is SELECTED from the list -- despite
    // item-value="code" being set, confirmed live 2026-08-22 (picking "Nifty
    // 50 (verified)" sent the whole { code, label, name } object to the
    // backend, which Sequelize correctly rejected as an invalid VARCHAR
    // value rather than silently stringifying it). form.index must always be
    // a plain label string -- everything downstream (preview/start/link-index
    // request bodies) sends it as one -- so this normalizes either shape
    // v-combobox might hand back, rather than trusting item-value alone.
    indexModel: {
      get() {
        return this.form.index;
      },
      set(val) {
        this.form.index = (val && typeof val === 'object') ? (val.code || val.label || '') : (val || '');
      },
    },
    // Sector equivalent of indexModel above -- same defensive object-vs-
    // string handling (the v-combobox bug fixed there must not be
    // reintroduced here), plus the auto-fill this feature actually adds:
    // picking a known sector fills the Codes textarea from its own `codes`
    // list, straight off the picked item so no separate catalog lookup is
    // needed for that case. REPLACES the textarea rather than appending --
    // picking a second sector is a fresh start, not an accumulation of
    // codes across unrelated sectors.
    sectorModel: {
      get() {
        return this.form.sector;
      },
      set(val) {
        if (val && typeof val === 'object') {
          this.form.sector = val.label || '';
          if (Array.isArray(val.codes)) this.form.codesRaw = val.codes.join(', ');
          return;
        }
        const label = val || '';
        this.form.sector = label;
        const known = this.sectorOptions.find((s) => s.label === label);
        if (known) this.form.codesRaw = known.codes.join(', ');
      },
    },
    eligibleCodes() {
      return (this.preview?.rows || []).filter((r) => r.result === 'eligible').map((r) => r.code);
    },
    unlinkedAlreadyOnboardedCodes() {
      return (this.preview?.rows || [])
        .filter((r) => r.result === 'alreadyOnboarded' && !r.alreadyLinkedToThisIndex)
        .map((r) => r.code);
    },
    unlinkedAlreadyOnboardedCount() {
      return this.unlinkedAlreadyOnboardedCodes.length;
    },
    unlinkedAlreadyOnboardedForSectorCodes() {
      return (this.preview?.rows || [])
        .filter((r) => r.result === 'alreadyOnboarded' && r.alreadyLinkedToThisSector === false)
        .map((r) => r.code);
    },
    unlinkedAlreadyOnboardedForSectorCount() {
      return this.unlinkedAlreadyOnboardedForSectorCodes.length;
    },
    // Sectors actually present among onboarded stocks -- deliberately a
    // DIFFERENT source from sectorOptions (the full ~20-entry catalog used
    // by the picker above, which must offer sectors nothing has been
    // onboarded from yet). The filter shouldn't offer a sector with zero
    // matching rows to show.
    distinctSectors() {
      const set = new Set();
      for (const u of this.universe) {
        for (const s of (u.sectors || [])) set.add(s.label);
      }
      return [...set].sort();
    },
    filteredUniverse() {
      // Inactive rows (out-of-range/intraday-restricted codes stored for a
      // complete index/sector list, see runOnboardingAsync) are hidden by
      // default -- showInactive opts back in. Independent of the sector
      // filter, which still applies on top either way.
      let list = this.showInactive ? this.universe : this.universe.filter((u) => u.active);
      if (this.sectorFilter) list = list.filter((u) => (u.sectors || []).some((s) => s.label === this.sectorFilter));
      return list;
    },
    runProgressPct() {
      if (!this.run || !this.run.total) return 0;
      return Math.round((this.run.done / this.run.total) * 100);
    },
    marketHoursBlocked() {
      const day = this.now.getDay();
      if (day === 0 || day === 6) return false;
      const mins = this.now.getHours() * 60 + this.now.getMinutes();
      return mins >= 9 * 60 && mins <= 15 * 60 + 40;
    },
  },
  mounted() {
    this.loadIndices();
    this.loadSectors();
    this.loadUniverse();
    this.clockTimer = setInterval(() => { this.now = new Date(); }, 60000);
  },
  beforeUnmount() {
    if (this.pollTimer) clearTimeout(this.pollTimer);
    if (this.clockTimer) clearInterval(this.clockTimer);
  },
  methods: {
    resultLabel(result) { return RESULT_LABELS[result] || result; },
    resultColor(result) { return RESULT_COLORS[result] || 'default'; },
    universeStatusColor(status) {
      return { Ready: 'success', 'Limited data': 'warning', 'History pending': 'default', Inactive: 'default' }[status] || 'default';
    },
    loadIndices() {
      fetchWrapper.get('api/gap-backtest/onboard/indices').then((res) => {
        const verified = (res?.data || []).map((i) => ({ code: i.code, label: `${i.name} (verified)`, name: i.name }));
        this.indexOptions = [...this.indexOptions, ...verified];
      }).catch((err) => {
        console.error('Failed to load indices:', err);
      });
    },
    // Backs the sector picker's item list AND its auto-fill -- each entry
    // already carries its own `codes`, sourced from nifty-constituents.js's
    // `industry` field (see getSectorCatalog() in index-onboarding.js).
    loadSectors() {
      fetchWrapper.get('api/gap-backtest/onboard/sectors').then((res) => {
        this.sectorOptions = res?.data || [];
      }).catch((err) => {
        console.error('Failed to load sectors:', err);
      });
    },
    loadUniverse() {
      this.loadingUniverse = true;
      return fetchWrapper.get('api/gap-backtest/onboard/universe').then((res) => {
        this.universe = res?.data || [];
        this.loadingUniverse = false;
      }).catch((err) => {
        console.error('Failed to load current universe:', err);
        this.loadingUniverse = false;
      });
    },
    // Same operation the scheduled cPanel cron runs against
    // /api/cron/gap-fill-backtest -- pure DB read+write, no broker calls --
    // exposed here so re-scoring after an onboarding batch doesn't mean
    // waiting for the next scheduled run.
    runBacktestNow() {
      this.runningBacktest = true;
      this.backtestError = null;
      fetchWrapper.post('api/gap-backtest/run-backtest').then((res) => {
        this.backtestSummary = res?.data || null;
        this.runningBacktest = false;
        this.loadUniverse();
      }).catch((err) => {
        this.backtestError = typeof err === 'string' ? err : 'Failed to run the backtest.';
        this.runningBacktest = false;
      });
    },
    parsedCodes() {
      return this.form.codesRaw
        .split(/[,\n]/)
        .map((c) => c.trim().toUpperCase())
        .filter(Boolean);
    },
    runPreview() {
      const codes = this.parsedCodes();
      if (!codes.length) {
        this.previewError = 'Paste at least one code first.';
        return;
      }
      this.previewing = true;
      this.previewError = null;
      this.linkError = null;
      this.linkResult = null;
      this.linkSectorError = null;
      this.linkSectorResult = null;
      fetchWrapper.post('api/gap-backtest/onboard/preview', {
        codes, index: this.form.index, sector: this.form.sector || null,
        minPrice: this.form.minPrice, maxPrice: this.form.maxPrice,
      }).then((res) => {
        this.preview = res?.data || null;
        this.previewing = false;
      }).catch((err) => {
        this.previewError = typeof err === 'string' ? err : 'Preview failed.';
        this.previewing = false;
      });
    },
    // Tags already-onboarded codes with the currently-selected index label,
    // without redoing any part of onboarding -- see gap-backtest.
    // controllers.js's onboardLinkIndex. Re-previews afterward so the
    // "already onboarded" rows immediately reflect the new link.
    linkExistingToIndex() {
      const codes = this.unlinkedAlreadyOnboardedCodes;
      if (!codes.length) return;
      this.linking = true;
      this.linkError = null;
      this.linkResult = null;
      fetchWrapper.post('api/gap-backtest/onboard/link-index', { codes, index: this.form.index }).then((res) => {
        this.linkResult = res?.data || null;
        this.linking = false;
        this.runPreview();
      }).catch((err) => {
        this.linkError = typeof err === 'string' ? err : 'Failed to link index.';
        this.linking = false;
      });
    },
    // Sector equivalent of linkExistingToIndex above -- see
    // gap-backtest.controllers.js's onboardLinkSector.
    linkExistingToSectorAction() {
      const codes = this.unlinkedAlreadyOnboardedForSectorCodes;
      if (!codes.length) return;
      this.linkingSector = true;
      this.linkSectorError = null;
      this.linkSectorResult = null;
      fetchWrapper.post('api/gap-backtest/onboard/link-sector', { codes, sector: this.form.sector }).then((res) => {
        this.linkSectorResult = res?.data || null;
        this.linkingSector = false;
        this.runPreview();
      }).catch((err) => {
        this.linkSectorError = typeof err === 'string' ? err : 'Failed to link sector.';
        this.linkingSector = false;
      });
    },
    runStart() {
      if (!this.eligibleCodes.length) return;
      this.starting = true;
      fetchWrapper.post('api/gap-backtest/onboard/start', {
        codes: this.eligibleCodes, index: this.form.index, sector: this.form.sector || null, minPrice: this.form.minPrice,
        maxPrice: this.form.maxPrice, years: this.form.years,
      }).then((res) => {
        this.starting = false;
        const runId = res?.data?.runId;
        if (runId) this.pollStatus(runId);
      }).catch((err) => {
        this.starting = false;
        this.previewError = typeof err === 'string' ? err : 'Could not start onboarding.';
      });
    },
    pollStatus(runId) {
      fetchWrapper.get(`api/gap-backtest/onboard/status?runId=${encodeURIComponent(runId)}`).then((res) => {
        this.run = res?.data || null;
        if (this.run && this.run.running) {
          this.pollTimer = setTimeout(() => this.pollStatus(runId), 3000);
        } else {
          this.loadUniverse();
        }
      }).catch((err) => {
        console.error('Failed to poll onboarding status:', err);
        this.pollTimer = setTimeout(() => this.pollStatus(runId), 5000);
      });
    },
  },
};
</script>
